import React, { useState, FocusEvent, useMemo, useEffect } from 'react'
import _ from 'lodash'

export interface IInputField {
  type: string
  label?: string
  initialValue?: string
  name?: string
  setValue?: (v: string) => void
  value?: string
  setCurrentValue?: React.Dispatch<React.SetStateAction<string>>
  success?: string
  error?: string
  info?: string
  removeError?: Function | null
  required?: boolean
  style?: any
  disabled?: boolean
  placeholder?: string
  pattern?: string
  title?: string
  icon?: string
  helperText?: string
}

const InputField = ({
  label = '',
  type,
  initialValue = '',
  name = '',
  setValue,
  value,
  setCurrentValue,
  success = '',
  error = '',
  info = '',
  removeError = null,
  required = false,
  style = {},
  disabled = false,
  placeholder = '',
  pattern,
  title,
  icon,
  helperText,
}: IInputField) => {
  const [isFocused, setIsFocused] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(true)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    if (success) setShowSuccess(true)
  }, [success])

  useEffect(() => {
    if (error) setShowError(true)
  }, [error])

  useEffect(() => {
    if (info) setShowInfo(true)
  }, [info])

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    if (removeError) {
      removeError()
    }
    if (setValue) {
      setValue(event.target.value)
    }
  }

  const id = 'inputfield-' + useMemo(() => _.uniqueId(), [])
  const hasValue = (value || initialValue || '').length > 0

  return (
    <div
      className={`modern-input-field ${disabled ? 'disabled' : ''} ${
        isFocused ? 'focused' : ''
      } ${hasValue ? 'has-value' : ''} ${showError && error ? 'error' : ''} ${
        showSuccess && success ? 'success' : ''
      } ${showInfo && info ? 'info' : ''}`}
      style={style}
    >
      {label && (
        <label htmlFor={id} className="modern-input-label">
          {label}
          {required && <span className="required-indicator">*</span>}
        </label>
      )}

      <div className="modern-input-container">
        {icon && (
          <span className="material-icons modern-input-icon">{icon}</span>
        )}

        <input
          id={id}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onChange={(e) => {
            if (setCurrentValue) setCurrentValue(e.target.value)
            if (setValue) setValue(e.target?.value)
          }}
          value={value !== undefined ? value : initialValue}
          name={name}
          placeholder={placeholder}
          type={type}
          required={required}
          pattern={pattern}
          title={title}
        />

        {showError && error && (
          <span className="modern-input-status-icon material-icons error">
            error
          </span>
        )}
        {showSuccess && success && (
          <span className="modern-input-status-icon material-icons success">
            check_circle
          </span>
        )}
        {showInfo && info && (
          <span className="modern-input-status-icon material-icons info">
            info
          </span>
        )}
      </div>

      {(showError && error) || (showSuccess && success) || (showInfo && info) || helperText ? (
        <div className="modern-input-message">
          {showError && error ? (
            <span className="error-message">{error}</span>
          ) : showSuccess && success ? (
            <span className="success-message">{success}</span>
          ) : showInfo && info ? (
            <span className="info-message">{info}</span>
          ) : helperText ? (
            <span className="helper-text">{helperText}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default React.memo(InputField)
